import apiResponse from '@stores/ApiResponse';
import { useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { GroupStore } from '@app/api/GroupStore';
import { CohortUser } from '@app/models/CohortUser';

type WeekResult = {
  week: number;
  success: boolean;
  jailCardUsed: boolean;
};

type ParticipantWeekResults = {
  cohortUserId: number;
  userName: string;
  weeks: WeekResult[];
};

type ParticipantPayout = {
  cohortUserId: number;
  userName: string;
  remainingPledge: number;
  winnerShare: number;
  giftCardShare: number;
  donationShare: number;
  totalPayout: number;
};

type LeaderDetails = {
  participant: CohortUser;
  successfulWeeks: number;
  currentStreak: number;
  rank?: number;
};

export enum TeamCelebrationResponseType {
  Incomplete = 'Incomplete',
  InPerson = 'Your captain will receive the funds and plan an in-person get-together—coffee, happy hour, a movie, or whatever your team decides!',
  GiftCard = 'Everyone receives a gift card for their share of the pot. You can each grab your favorite treat (Starbucks, Amazon snacks, etc.) and meet virtually to celebrate together.',
  Donation = 'Everyone donates their portion to a cause of their choice! Then gather virtually to share which organizations you chose and why.',
}

export type AdminCohortReportCard = {
  currentPot: number;
  halfPot: number;
  leaderboard: Map<number, LeaderDetails>;
  winners: LeaderDetails[];
  teamCelebrationResult: TeamCelebrationResponseType;
  participantWeekResults: ParticipantWeekResults[];
  payouts: ParticipantPayout[];
};

export class AdminCohortReportCardViewCreator {
  constructor(private readonly store: GroupStore = new GroupStore()) {}

  async createView(groupId: number) {
    const result = await apiResponse.handleApiResponse<AdminCohortReportCard>(
      async () => await this.store.AdminGetCohortReportCard(groupId)
    );

    return result as AdminCohortReportCard;
  }
}

export const useAdminCohortReportCardViewCreator = (groupId: number, enabled: boolean = true) =>
  useQuery({
    queryKey: [AdminCohortReportCardViewCreator.name, groupId],
    queryFn: () => new AdminCohortReportCardViewCreator().createView(groupId),
    staleTime: Duration.fromObject({ minutes: 1 }).milliseconds,
    enabled: enabled && groupId > 0,
  });
